import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/ui.css';
import './ViewSubmissions.css';

const normalize = (s) => String(s || '').toLowerCase().trim();

const ASSIGNABLE_STATUS_SET = new Set([
  'accept submission for review',
  'awaiting review',
  'awaiting assignment',
  'under review',
]);

const ADMIN_REVIEWABLE_STATUS_SET = new Set([
  'submitted',
  'request resubmit',
  'resubmitted',
  'accept submission for review',
]);

const ADMIN_VIEW_ONLY_STATUSES = new Set([
  'awaiting review',
  'awaiting assignment',
  'under review',
  'pending meeting',
  'request resubmit (post-meeting)',
  'resubmitted (post-meeting)',
  'pending final approval',
  'approved',
  'rejected',
  'completed study',
]);

const canAdminReviewRow = (row) => {
  const st = normalize(row.status);
  if (ADMIN_VIEW_ONLY_STATUSES.has(st)) return false;
  return ADMIN_REVIEWABLE_STATUS_SET.has(st);
};

function useSidebarWidthVar() {
  React.useEffect(() => {
    const selectors = ['#sidebar', '.sidebar', 'aside.sidebar', '.side-nav', '.sidenav'];
    const getSidebar = () => selectors
      .map((s) => document.querySelector(s))
      .find(Boolean);

    const setVar = () => {
      const el = getSidebar();
      const w = el ? Math.round(el.getBoundingClientRect().width) : 0;
      document.documentElement.style.setProperty('--sidebar-w', `${w}px`);
    };

    setVar();
    window.addEventListener('resize', setVar);
    return () => window.removeEventListener('resize', setVar);
  }, []);
}

function chipColors(status) {
  const s = normalize(status);
  const explicit = {
    'completed study':                 { bg:'#eef7ff', color:'#1f4c77', border:'#cfe6ff' },
    'pending final approval':          { bg:'#fff7e6', color:'#8a5a00', border:'#ffe2b5' },
    'request resubmit':                { bg:'#ffeef2', color:'#b40033', border:'#ffd2dc' },
    'request resubmit (post-meeting)': { bg:'#ffeef2', color:'#b40033', border:'#ffd2dc' },
    approved:                          { bg:'#ecfdf5', color:'#065f46', border:'#a7f3d0' },
    rejected:                          { bg:'#ffeff0', color:'#9e061c', border:'#ffd0d5' },
    submitted:                         { bg:'#e8f3fb', color:'#215e86', border:'#c9e2f5' },
    'under review':                    { bg:'#fff3e8', color:'#7a3e06', border:'#ffd9bf' },
    'awaiting review':                 { bg:'#f0e7ff', color:'#42337a', border:'#ddcdfc' },
    'awaiting assignment':             { bg:'#f0e7ff', color:'#42337a', border:'#ddcdfc' },
    'accept submission for review':    { bg:'#f0e7ff', color:'#42337a', border:'#ddcdfc' },
  };
  if (explicit[s]) return explicit[s];

  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  const hues = [210, 260, 20, 140, 340, 30, 180, 300, 100, 0];
  const h = hues[hash % hues.length];
  return {
    bg:     `hsl(${h} 85% 96%)`,
    color:  `hsl(${h} 35% 28%)`,
    border: `hsl(${h} 75% 85%)`,
  };
}

const ViewSubmissions = () => {
  useSidebarWidthVar();
  const [submissions, setSubmissions] = useState([]);
  const [sortBy, setSortBy] = useState('submission_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const roleId = parseInt(localStorage.getItem('role_id'), 10);
  const userId = localStorage.getItem('researcherId');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

  const isAssignMode = new URLSearchParams(location.search).get('assign') === '1';

  useEffect(() => {
    const fetchSubmissions = async () => {
      const endpoint =
        roleId === 2 || roleId === 4
          ? `${API_BASE_URL}/submissions/all`
          : roleId === 3
          ? `${API_BASE_URL}/assigned-proposals/${userId}`
          : `${API_BASE_URL}/submissions/user/${userId}`;

      try {
        const res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const data = await res.json();
        setSubmissions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching submissions:', err);
        setSubmissions([]);
      }
    };

    fetchSubmissions();
  }, [roleId, userId, API_BASE_URL]);

  const getSortedSubmissions = () =>
    [...submissions].sort((a, b) => {
      let aValue, bValue;
      if (sortBy === 'researcher') {
        aValue = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        bValue = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
      } else if (sortBy === 'submission_date') {
        aValue = new Date(a.submission_date);
        bValue = new Date(b.submission_date);
      } else {
        const aval = a[sortBy];
        const bval = b[sortBy];
        aValue = typeof aval === 'string' ? aval.toLowerCase() : (aval ?? '');
        bValue = typeof bval === 'string' ? bval.toLowerCase() : (bval ?? '');
      }
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const getFilteredSortedSubmissions = () => {
    const q = (searchTerm || '').toLowerCase().trim();
    const list = getSortedSubmissions();
    if (!q) return list;

    return list.filter((s) => {
      const title = (s.title || '').toLowerCase();
      const status = (s.status || '').toLowerCase();
      const customId = (s.custom_id || '').toLowerCase();
      const researcher = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();

      if (roleId === 2 || roleId === 4) {
        return title.includes(q) || status.includes(q) || customId.includes(q) || researcher.includes(q);
      }
      return title.includes(q) || status.includes(q) || customId.includes(q);
    });
  };

  const sortedFiltered = getFilteredSortedSubmissions();

  const rows =
    roleId === 4 && isAssignMode
      ? sortedFiltered.filter((s) => ASSIGNABLE_STATUS_SET.has(normalize(s.status)))
      : sortedFiltered;

  const pageTitle =
    roleId === 4
      ? isAssignMode ? '' : ''
      : roleId === 2
      ? 'All Submissions'
      : roleId === 3
      ? 'Assigned Proposals'
      : 'My Submissions';

  const handleAction = (proposalId, action) => {
    if (action === 'view') navigate(`/proposal/${proposalId}`);
    else if (action === 'resubmit') navigate(`/proposal/resubmit/${proposalId}`);
    else if (action === 'review') navigate(`/review-proposal/${proposalId}`);
    else if (action === 'assign') navigate(`/proposal/assign/${proposalId}`);
  };

  const statusClass = (s) => normalize(s).replace(/\s+/g, '-');

  return (
    <div className="view-submissions-container wide">
      <div className="card head-card">
        <div className="vs-header column">
          <h2 className="page-title">{pageTitle}</h2>

          {roleId === 4 && (
            <div className="vs-toggle">
              <button
                className={`vs-pill xs wide ${!isAssignMode ? 'active' : ''}`}
                onClick={() => navigate('/proposal/view-submissions')}
              >
                List All
              </button>
              <button
                className={`vs-pill xs wide ${isAssignMode ? 'active' : ''}`}
                onClick={() => navigate('/proposal/view-submissions?assign=1')}
              >
                Assign
              </button>
            </div>
          )}
        </div>
        {submissions.length > 0 && (
          <div className="filters-grid">
            <label className="control grow">
              <span className="control-label">Search</span>
              <input
                type="text"
                className="input-std"
                placeholder={
                  roleId === 2 || roleId === 4
                    ? 'Title, status, custom ID, researcher…'
                    : 'Title, status, custom ID…'
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label>

            <label className="control">
              <span className="control-label">Sort by</span>
              <select
                className="select-std"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="submission_date">Submission Date</option>
                <option value="title">Title</option>
                {(roleId === 2 || roleId === 4) && <option value="researcher">Researcher</option>}
                <option value="status">Status</option>
              </select>
            </label>

            <label className="control">
              <span className="control-label">Order</span>
              <select
                className="select-std"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="card">
        {submissions.length === 0 ? (
          <p className="muted">No submissions found.</p>
        ) : (
          <table className="table-std submissions-table dense">
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>ID</th>
                {(roleId === 2 || roleId === 4) && <th>Researcher</th>}
                <th>Submission Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="center">
                    {roleId === 4 && isAssignMode
                      ? 'No proposals need assignment right now.'
                      : 'No submissions match your search.'}
                  </td>
                </tr>
              ) : (
                rows.map((s, i) => {
                  const chip = chipColors(s.status);
                  const adminCanReview = roleId === 2 && canAdminReviewRow(s);

                  return (
                    <tr key={s.proposal_id}>
                      <td>{i + 1}</td>
                      <td>{s.title}</td>
                      <td>{s.custom_id}</td>
                      {(roleId === 2 || roleId === 4) && <td>{s.first_name} {s.last_name}</td>}
                      <td>{s.submission_date ? new Date(s.submission_date).toLocaleDateString() : '-'}</td>
                      <td>
                        <span
                          className={`status-badge ${statusClass(s.status)}`}
                          style={{ background: chip.bg, color: chip.color, borderColor: chip.border }}
                        >
                          {s.status}
                        </span>
                      </td>
                      <td className="actions">
                        {roleId === 2 ? (
                          <button
                            className={adminCanReview ? 'btn xs' : 'btn ghost xs'}
                            onClick={() => handleAction(s.proposal_id, adminCanReview ? 'review' : 'view')}
                            title={adminCanReview ? 'Open initial check' : 'Open read-only details'}
                          >
                            {adminCanReview ? 'Review' : 'View'}
                          </button>
                        ) : roleId === 3 ? (
                          <button className="btn xs" onClick={() => navigate(`/review-assigned-proposal/${s.proposal_id}`)}>Review</button>
                        ) : roleId === 4 ? (
                          isAssignMode ? (
                            <button
                              className="btn primary xs"
                              onClick={() => handleAction(s.proposal_id, 'assign')}
                              title={normalize(s.status) === 'under review' ? 'Unassign' : 'Assign'}
                            >
                              {normalize(s.status) === 'under review' ? 'Unassign' : 'Assign'}
                            </button>
                          ) : (
                            <button className="btn ghost xs" onClick={() => handleAction(s.proposal_id, 'view')}>View</button>
                          )
                        ) : (s.status === 'Request Resubmit' || s.status === 'Request Resubmit (Post-Meeting)') ? (
                          <button className="btn primary xs" onClick={() => handleAction(s.proposal_id, 'resubmit')}>Resubmit</button>
                        ) : (
                          <button className="btn ghost xs" onClick={() => handleAction(s.proposal_id, 'view')}>View</button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ViewSubmissions;
